import type { ResolvedRemovalOrder } from "../types/removal";
import {
  findCatalogRowById,
  resolveTaxableFromSources,
} from "../utils/readResolveTaxable";
import { getResolvedDiscountDisplay } from "./getResolvedDiscountDisplay";

const round2 = (n: number) => Math.round(Number(n) * 100) / 100;

export type RemovalWorkOrderResolveFormikSync = {
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
};

export type ApplyRemovalWorkOrderResolveOptions = {
  requestedDiscountCode?: string;
  existingFeeRows?: unknown[] | null;
  specialFeeCatalog?: unknown[] | null;
};

export type ApplyRemovalWorkOrderResolveResult = {
  feeSubtotal: number;
  removalTax: number;
  feeTax: number;
  grandTotal: number;
  overallTax: number;
  mergedFees: Array<{
    id: string;
    name: string;
    amount: number;
    taxable: boolean;
    tax_percentage: number | null;
    tax_amount: number;
    required: boolean;
  }>;
};

/**
 * Write backend removal resolve response into Formik totals on removal work order pages.
 * Uses `totals.removal_*` field names (distinct from removal add/edit request pages).
 */
export function applyRemovalWorkOrderResolveResponse(
  resolved: ResolvedRemovalOrder,
  formik: RemovalWorkOrderResolveFormikSync,
  fallbackRemovalSubtotal: number,
  options?: ApplyRemovalWorkOrderResolveOptions
): ApplyRemovalWorkOrderResolveResult {
  const feeRows = Array.isArray(resolved.specialfee) ? resolved.specialfee : [];
  const feeSubtotal = feeRows.reduce((s, f) => s + Number(f.specialfee_amount || 0), 0);
  const removalTax = Number(resolved.removal_tax_amount || 0);
  const feeTax = feeRows.reduce((s, f) => s + Number(f.specialfee_tax_amount || 0), 0);
  const overallTax = Number(resolved.over_all_tax_amount ?? removalTax + feeTax);
  const removalSub = Number(resolved.removal_cost ?? fallbackRemovalSubtotal);
  const grandTotal = Number(
    resolved.total_amount ??
      removalSub + feeSubtotal + overallTax - Number(resolved.discount_amount || 0)
  );

  const mergedFees = feeRows.map((f) => {
    const id = String(f.specialfee_id || "");
    const existingRow = findCatalogRowById(options?.existingFeeRows, id, [
      "id",
      "_id",
      "specialfee_id",
    ]);
    const catalogRow = findCatalogRowById(options?.specialFeeCatalog, id, [
      "id",
      "_id",
      "specialfee_id",
    ]);
    return {
      id,
      name: String(f.specialfee_name || ""),
      amount: Number(f.specialfee_amount || 0),
      taxable: resolveTaxableFromSources(
        [existingRow, catalogRow, f as Record<string, unknown>],
        ["taxable", "specialfee_taxable", "is_taxable"],
        true
      ),
      tax_percentage: f.specialfee_tax_percentage ?? null,
      tax_amount: Number(f.specialfee_tax_amount || 0),
      required: f.required === true,
    };
  });

  formik.setFieldValue("totals.removal_subtotal", removalSub, false);
  formik.setFieldValue("totals.removal_tax_amount", removalTax, false);
  formik.setFieldValue("totals.removal_tax_percentage", resolved.removal_tax_percentage ?? "", false);
  formik.setFieldValue("totals.specialfee_subtotal", feeSubtotal, false);
  formik.setFieldValue("totals.specialfee_tax_amount", feeTax, false);
  formik.setFieldValue("totals.over_all_tax_amount", overallTax, false);
  formik.setFieldValue("totals.over_all_tax_percentage", resolved.over_all_tax_percentage ?? "", false);
  formik.setFieldValue("totals.grand_total", grandTotal, false);
  formik.setFieldValue("specialfee", mergedFees, false);
  formik.setFieldValue("tax", overallTax, false);

  if (resolved.subtotal_amount != null) {
    formik.setFieldValue("totals.subtotal_amount", Number(resolved.subtotal_amount), false);
    formik.setFieldValue("subtotal_amount", Number(resolved.subtotal_amount), false);
  }

  const requestedDiscountCode =
    options?.requestedDiscountCode !== undefined
      ? String(options.requestedDiscountCode).trim()
      : undefined;

  if (requestedDiscountCode === "") {
    formik.setFieldValue("discount_code", "", false);
    formik.setFieldValue("discount_amount", 0, false);
  } else {
    const { code: discountCode, amount: discountAmount } = getResolvedDiscountDisplay(
      resolved as Parameters<typeof getResolvedDiscountDisplay>[0],
      requestedDiscountCode
    );
    formik.setFieldValue("discount_amount", round2(discountAmount), false);
    if (discountCode) {
      formik.setFieldValue("discount_code", discountCode, false);
    } else if (requestedDiscountCode) {
      formik.setFieldValue("discount_code", requestedDiscountCode, false);
    }
  }

  formik.setFieldValue("calc_removal_rate_str", resolved.removal_tax_percentage ?? "", false);
  formik.setFieldValue("calc_overall_rate_str", resolved.over_all_tax_percentage ?? "", false);

  if (resolved.cart_id) {
    formik.setFieldValue("totals.cartId", String(resolved.cart_id), false);
  }

  return {
    feeSubtotal,
    removalTax,
    feeTax,
    grandTotal,
    overallTax,
    mergedFees,
  };
}
